import { ref, shallowRef, onScopeDispose, getCurrentScope } from 'vue'

/**
 * Reading something out loud, with the voice the person already has.
 *
 * Dos voces, una sola puerta.
 *
 * `speak(text)` usa la síntesis del propio navegador: no cuesta nada por
 * palabra, funciona con la tapa bajada y con mala cobertura, y ninguna parte de
 * la frase sale de la máquina donde se dice — que importa cuando la frase es lo
 * que un cliente debe. Lo que se paga es la calidad: sale la voz que instalaron
 * Windows, macOS, Android o iOS, y varía.
 *
 * `play(bytes)` dice lo que ya viene dicho: el audio que sintetizó el gateway
 * (`platform.ai.speak`, §11). Suena como una persona, cuesta por carácter y el
 * texto ha salido a un proveedor — decisión de la instancia, no del ui-kit.
 *
 * Están las dos detrás del MISMO composable a propósito, compartiendo `speaking`
 * y `stop()`. Quien conduce una conversación tiene que poder esperar a que
 * termine de hablar sin saber cuál de las dos voces le tocó, y sobre todo
 * apagarla: una pantalla con dos maneras de callar a alguien es una pantalla
 * donde una de las dos se olvida.
 *
 * ── The three things that make this harder than `speak(text)` ───────────────
 *
 *   1. Voices load late. `getVoices()` answers with an empty array on the first
 *      call in most browsers and fills in on `voiceschanged`, which means the
 *      first thing anybody ever asks to be read is exactly the thing that gets
 *      read in the wrong accent.
 *   2. Chrome stops mid-sentence. An utterance past roughly fifteen seconds is
 *      cut off with no error and no `onend`. So nothing long is ever sent as
 *      one utterance: it is split at sentence boundaries and queued.
 *   3. It can be refused. Safari treats speech like audio playback and blocks
 *      it without a recent user gesture — and an answer arrives long after the
 *      gesture that asked for it. A refusal has to look like a button to press,
 *      never like silence.
 */

/** Past this, Chrome cuts an utterance off mid-word. Well under it on purpose. */
const CHUNK_CHARS = 180

const synth = () => (typeof window === 'undefined' ? null : window.speechSynthesis)

export function useSpeech() {
  const speaking = ref(false)
  const blocked = ref(false)
  const voices = shallowRef([])

  const supported = Boolean(synth() && typeof window.SpeechSynthesisUtterance === 'function')

  /** The queue in flight, so a second request replaces the first rather than talking over it. */
  let queue = []
  let token = 0

  function loadVoices() {
    const engine = synth()
    if (!engine) return
    const found = engine.getVoices()
    if (found.length) voices.value = found
  }

  if (supported) {
    loadVoices()
    // Fires once the list is really there. Assigning rather than adding a
    // listener would trample anybody else's; this is the platform's one reader.
    synth().addEventListener?.('voiceschanged', loadVoices)
  }

  /**
   * The best voice for a language, or none and let the engine decide.
   *
   * Exact region first (`ca-ES` over `ca-AD`), then the language, then nothing.
   * Catalan is the case that matters here: most desktops have no Catalan voice
   * at all, and a Catalan sentence read by the Spanish voice is understandable,
   * while one read by the English voice is not.
   */
  function voiceFor(locale) {
    const list = voices.value
    if (!list.length) return null

    const wanted = String(locale ?? '').replace('_', '-').toLowerCase()
    const language = wanted.slice(0, 2)

    return list.find(v => v.lang.toLowerCase() === wanted)
      ?? list.find(v => v.lang.toLowerCase().startsWith(`${language}-`))
      ?? list.find(v => v.lang.toLowerCase().startsWith(language))
      ?? (language === 'ca' ? list.find(v => v.lang.toLowerCase().startsWith('es')) : null)
      ?? null
  }

  /** Sentence-sized pieces, so nothing is ever cut off mid-word. */
  function chunk(text) {
    const pieces = []
    let rest = String(text ?? '').trim()

    while (rest.length > CHUNK_CHARS) {
      const window = rest.slice(0, CHUNK_CHARS)
      const at = Math.max(
        window.lastIndexOf('. '), window.lastIndexOf('? '),
        window.lastIndexOf('! '), window.lastIndexOf('; ')
      )
      const cut = at > CHUNK_CHARS * 0.4 ? at + 1 : (window.lastIndexOf(' ') + 1 || CHUNK_CHARS)
      pieces.push(rest.slice(0, cut).trim())
      rest = rest.slice(cut).trim()
    }
    if (rest) pieces.push(rest)
    return pieces
  }

  /** El audio que está sonando, si lo hay. Uno y solo uno: ver `play()`. */
  let playing = null

  function stop() {
    token += 1
    queue = []
    speaking.value = false
    synth()?.cancel()

    if (playing) {
      const { element, url } = playing
      playing = null
      element.pause()
      // Se suelta siempre. Un blob que no se revoca se queda en memoria hasta
      // que se recarga la página, y una conversación de quince vueltas son
      // quince audios: en un móvil de obra eso se nota.
      element.src = ''
      URL.revokeObjectURL(url)
    }
  }

  /**
   * Decir algo que YA viene dicho: los bytes que sintetizó el gateway.
   *
   * Comparte `speaking` y `stop()` con `speak()`, que es todo el propósito de
   * que viva aquí: quien conduce una conversación espera a que termine de
   * hablar sin tener que saber cuál de las dos voces sonó.
   *
   * @param {Blob|ArrayBuffer|Uint8Array|string} audio  bytes, o base64
   * @param {object} [options]
   * @param {string} [options.mimeType='audio/mpeg']
   * @returns {Promise<void>} resuelve cuando termina de sonar, o cuando no puede
   */
  function play(audio, { mimeType = 'audio/mpeg' } = {}) {
    stop()
    const mine = token
    blocked.value = false

    const blob = toBlob(audio, mimeType)
    if (!blob) return Promise.resolve()

    const url = URL.createObjectURL(blob)
    const element = new window.Audio(url)
    playing = { element, url }
    speaking.value = true

    return new Promise((resolve) => {
      // Termina SIEMPRE y una sola vez: un audio que no dispara `ended` —un
      // formato que este navegador no sabe, una pestaña que se duerme— dejaría
      // la conversación esperando para siempre a que se callara.
      let done = false
      const finish = () => {
        if (done) return
        done = true
        if (mine === token) { speaking.value = false; stop() }
        resolve()
      }

      element.onended = finish
      element.onerror = finish

      const started = element.play()
      // Safari y Chrome en móvil pueden negarse sin un gesto reciente. Es lo
      // mismo que le pasa a la voz del navegador, así que se cuenta igual: no
      // es silencio, es un botón que hay que pulsar.
      if (started?.catch) {
        started.catch(() => {
          blocked.value = true
          finish()
        })
      }
    })
  }

  /**
   * @param {string} text
   * @param {object} [options]
   * @param {string} [options.locale]  the interface language, e.g. 'ca'
   * @param {number} [options.rate=1]
   */
  function speak(text, { locale = 'es', rate = 1 } = {}) {
    const engine = synth()
    if (!engine || !text?.trim()) return

    stop()
    const mine = token
    blocked.value = false

    const voice = voiceFor(locale)
    queue = chunk(text).map((piece) => {
      const utterance = new window.SpeechSynthesisUtterance(piece)
      if (voice) utterance.voice = voice
      // `lang` still matters with a voice set: it is what decides how numbers
      // and currency are read, and «35.641,76 €» read as English is gibberish.
      utterance.lang = voice?.lang ?? locale
      utterance.rate = rate
      return utterance
    })

    if (!queue.length) return

    let index = 0
    const next = () => {
      // A newer request came in while this one was talking.
      if (mine !== token) return
      if (index >= queue.length) { speaking.value = false; return }

      const utterance = queue[index++]
      utterance.onend = next
      utterance.onerror = (event) => {
        // «not-allowed» is the browser refusing, not the engine failing: the
        // person has to press something. Anything else ends the reading rather
        // than leaving the button spinning for ever.
        if (event?.error === 'not-allowed') blocked.value = true
        speaking.value = false
        queue = []
      }
      engine.speak(utterance)
    }

    speaking.value = true
    next()
  }

  // Guarded because this is also called straight from a test, where there is no
  // effect scope to hang the cleanup on and Vue says so on every single call.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      stop()
      synth()?.removeEventListener?.('voiceschanged', loadVoices)
    })
  }

  return { supported, speaking, blocked, voices, speak, play, stop, voiceFor, chunk }
}

/**
 * Los bytes, vengan como vengan.
 *
 * Base64 porque es como viaja dentro de una respuesta JSON, que es como lo
 * manda un módulo que ya estaba contestando otra cosa en la misma vuelta —
 * partirlo en una segunda petición sería otro viaje de ida y vuelta en mitad de
 * una conversación.
 */
function toBlob(audio, mimeType) {
  if (!audio) return null
  if (typeof Blob !== 'undefined' && audio instanceof Blob) return audio
  if (audio instanceof ArrayBuffer || ArrayBuffer.isView(audio)) return new Blob([audio], { type: mimeType })

  if (typeof audio === 'string') {
    try {
      const binary = window.atob(audio)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
      return new Blob([bytes], { type: mimeType })
    } catch {
      // Un base64 a medias es un audio que no suena. Se devuelve nada y quien
      // llama se cae a la voz del navegador, que es la razón de que exista.
      return null
    }
  }

  return null
}

export { CHUNK_CHARS, toBlob }
